import { FormattedMessage } from 'react-intl';

import { MainSectionWrapper } from '../../pageStructure/mainSectionWrapper';

import messages from './DeclinedView.messages';
import styles from './DeclinedView.module.css';
import { Button, Icon } from '@iwoca/orion';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { useQuery } from '@tanstack/react-query';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import { fetchGetApplication } from '@iwoca/lapi-client/edge';
import { differenceInMonths } from 'date-fns';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { RequestedProduct } from '../MyOffers.types';
import { DECLINED_APPLICATION_REAPPLY } from '../MyOffers.actions';
import InternationalisedName from '../../../myAccount/multiLoanDashboard/dashboardComponents/InternationalisedName';
import {
    DE_PHONE_NUMBER,
    UK_PHONE_NUMBER
} from '../../../myAccount/iwocaContactInformation.constants';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    reapplyFlexi: () =>
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            location: {
                pathname: '/apply-flexi-loan/'
            }
        }),
    declinedReapply: (requestedProduct: RequestedProduct) =>
        dispatch({
            type: DECLINED_APPLICATION_REAPPLY,
            currentRequestedProduct: requestedProduct
        })
});

const DeclinedView = ({
    reapplyFlexi,
    declinedReapply
}: ReturnType<typeof mapDispatchToProps>) => {
    const stateKey = useStateKey();

    const { data: application } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () =>
            fetchGetApplication({
                stateKey
            })
    });
    const status = application?.data?.status;
    const statusDate = application?.data?.status_date;
    const declineStatus = application?.data?.decline_status;
    const requestedProduct = application?.data?.requested_products?.[0];

    const country = getCountry();

    const getReapplyEligibility = () => {
        if (status !== 'declined' || !declineStatus || !statusDate)
            return false;

        const monthsSinceStatusChange = differenceInMonths(
            new Date(),
            statusDate!
        );

        if (
            monthsSinceStatusChange >= 6 ||
            (country === 'UK' &&
                monthsSinceStatusChange >= 3 &&
                declineStatus === 'Soft Declined')
        )
            return true;

        return false;
    };

    const reapply = () => {
        if (requestedProduct?.product_type === 'merchant_cash_advance') {
            requestedProduct && declinedReapply(requestedProduct);
        } else {
            reapplyFlexi();
        }
    };

    const shouldSeeReapplyCard = getReapplyEligibility() && country === 'UK';

    return shouldSeeReapplyCard ? (
        <ReapplyCard ctaOnClick={reapply} />
    ) : (
        <DeclinedCard
            ctaOnClick={reapply}
            isCTAVisible={getReapplyEligibility()}
        />
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(DeclinedView);

const DeclinedCard = ({
    ctaOnClick,
    isCTAVisible
}: {
    ctaOnClick: () => void;
    isCTAVisible: boolean;
}) => {
    return (
        <MainSectionWrapper className={styles.wrapper}>
            <div className={styles.card}>
                <div className={styles.iconContainer}>
                    <Icon
                        icon="exclamationTriangleFilled"
                        className={styles.titleIcon}
                    />
                </div>

                <div className={styles.title}>
                    <FormattedMessage {...messages.title} />
                </div>

                <div className={styles.bodyText}>
                    <FormattedMessage
                        data-test-id="declined-body-text"
                        {...messages.description}
                        values={{
                            phoneNumber: (
                                <a
                                    href={
                                        getCountry() === 'DE'
                                            ? `tel:${DE_PHONE_NUMBER}`
                                            : `tel:${UK_PHONE_NUMBER}`
                                    }
                                    className={styles.phoneNumber}
                                >
                                    {getCountry() === 'DE'
                                        ? `${DE_PHONE_NUMBER}`
                                        : `${UK_PHONE_NUMBER}`}
                                </a>
                            )
                        }}
                    />
                </div>

                {isCTAVisible && (
                    <Button
                        size="large"
                        variant="primary"
                        className={styles.reApplyCTA}
                        data-testid="reapply-cta"
                        onClick={ctaOnClick}
                    >
                        <FormattedMessage {...messages.reApplyButtonText} />
                    </Button>
                )}
            </div>
        </MainSectionWrapper>
    );
};

const ReapplyCard = ({ ctaOnClick }: { ctaOnClick: () => void }) => {
    return (
        <MainSectionWrapper
            className={styles.wrapper}
            data-testid="reapply-card"
        >
            <div className={styles.card}>
                <div className={styles.iconContainer}>
                    <Icon icon="homeFilled" className={styles.titleIcon} />
                </div>

                <div className={styles.title}>
                    <FormattedMessage
                        {...messages.reapplyTitle}
                        values={{ name: <InternationalisedName /> }}
                    />
                </div>

                <div className={styles.bodyText}>
                    <FormattedMessage
                        data-test-id="declined-reapply-body-text"
                        {...messages.reapplyDescription}
                        values={{
                            phoneNumber: (
                                <a
                                    href={`tel:${UK_PHONE_NUMBER}`}
                                    className={styles.phoneNumber}
                                >
                                    {UK_PHONE_NUMBER}
                                </a>
                            )
                        }}
                    />
                </div>

                <Button
                    size="large"
                    variant="primary"
                    className={styles.reApplyCTA}
                    data-testid="reapply-cta"
                    onClick={ctaOnClick}
                >
                    <FormattedMessage {...messages.reApplyButtonText} />
                </Button>
            </div>
        </MainSectionWrapper>
    );
};
